import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { AdminDashboardPage } from 'src/modules/admin/pages/AdminDashboardPage';
import { AdminReportsPage } from 'src/modules/admin/pages/AdminReportsPage';
import { LegacyAdminDashboardPage } from 'src/modules/admin/pages/LegacyAdminDashboardPage';
import { PreSessionAssessmentsIntroPage } from 'src/modules/assessments/pages/PreSessionAssessmentsIntroPage';
import { PreSessionAssessmentsPage } from 'src/modules/assessments/pages/PreSessionAssessmentsPage';
import { PreSessionAssessmentsScorePage } from 'src/modules/assessments/pages/PreSessionAssessmentsScorePage';

import { CompletionPage } from 'src/modules/auth/screens/CompletionPage';
import { ConsentFormPage } from 'src/modules/auth/screens/ConsentFormPage';
import { IneligiblePage } from 'src/modules/auth/screens/IneligiblePage';
import { LandingPage } from 'src/modules/auth/screens/LandingPage';
import { PasswordFormPage } from 'src/modules/auth/screens/PasswordFormPage';
import { PreferredEmailPage } from 'src/modules/auth/screens/PreferredEmailPage';
import { PreferredEmailSentPage } from 'src/modules/auth/screens/PreferredEmailSentPage';
import { RegistrationLinkNotValidPage } from 'src/modules/auth/screens/RegistrationLinkNotValidPage';
import { RegistrationPage } from 'src/modules/auth/screens/RegistrationPage';
import { StartAssessmentPage } from 'src/modules/auth/screens/StartAssessmentPage';
import { CoursePage } from 'src/modules/courses/pages/CoursePage';
import { HomePage } from 'src/modules/dashboard/pages/HomePage';
import { IntakeFormPage } from 'src/modules/intakeForm/pages/IntakeFormPage';
import { GetProgramRecommendationsPage } from 'src/modules/onboarding/pages/GetProgramRecommendationsPage';
import { JoinACirclePage } from 'src/modules/onboarding/pages/JoinACirclePage';
import { OnboardingWellnessSurveyPage } from 'src/modules/onboarding/pages/OnboardingWellnessSurveyPage';
import { OnMyOwnPage } from 'src/modules/onboarding/pages/OnMyOwnPage';
import { PickYourTypeOfCarePage } from 'src/modules/onboarding/pages/PickYourTypeOfCarePage';
import { PrivacyPage } from 'src/modules/onboarding/pages/PrivacyPage';
import { ProvidePersonalEmailAddressPage } from 'src/modules/onboarding/pages/ProvidePersonalEmailAddressPage';
import YoureOnYourWay from 'src/modules/onboarding/pages/YoureOnYourWayPage';
import WorkWithMentalHealthCoachPage from 'src/modules/providers/pages/WorkWithMentalHealthCoachPage';
import { ReTriageAssessmentPage } from 'src/modules/reTriage/pages/ReTriageAssessmentPage';
import { ReTriageContinueTherapyPage } from 'src/modules/reTriage/pages/ReTriageContinueTherapyPage';
import { ReTriageGreenYellowSummaryPage } from 'src/modules/reTriage/pages/ReTriageGreenYellowSummaryPage';
import { ReTriageIntroPage } from 'src/modules/reTriage/pages/ReTriageIntroPage';
import { ReTriageRequestCareAdvisorPage } from 'src/modules/reTriage/pages/ReTriageRequestCareAdvisorPage';
import { ReTriageThankYouPage } from 'src/modules/reTriage/pages/ReTriageThankYouPage';
import { ReTriageTopicsSelectionPage } from 'src/modules/reTriage/pages/ReTriageTopicsSelectionPage';
import { DailyPause } from 'src/modules/scheduledContent/pages/DailyPause';
import { DailyPauseCelebration } from 'src/modules/scheduledContent/pages/DailyPauseCelebration';
import { RateVideoSessionPage } from 'src/modules/videoSessions/pages/RateVideoSessionPage';
import { RegistrationBasicsForm } from '../modules/auth/containers/RegistrationBasicsForm';

import { WellbeingScore } from '../modules/assessments/components/WellbeingScore';
import { RetakeAssessmentsPage } from '../modules/assessments/pages/RetakeAssessmentsPage';
import AccountHasExpired from '../modules/auth/containers/AccountHasExpired';
import EapPasswordResetForm from '../modules/auth/containers/EapPasswordResetForm';
import EapPasswordResetFormFinal from '../modules/auth/containers/EapPasswordResetFormFinal';
import LoginForm from '../modules/auth/containers/LoginForm';
import PostAuth from '../modules/auth/containers/PostAuth';
import RegistrationForm from '../modules/auth/containers/RegistrationForm';
import SingleSignOnFailed from '../modules/auth/containers/SingleSignOnFailed';
import SingleSignOnForm from '../modules/auth/containers/SingleSignOnForm';
import SingleSignOnVerify from '../modules/auth/containers/SingleSignOnVerify';
import EmailAddressConfirmed from '../modules/containers/EmailAddressConfirmed';
import ProvideAdditionalInfoPage from '../modules/dashboard/pages/ProvideAdditionalInfoPage';
import { Program as NewProgram } from '../modules/newPrograms/pages/Program';
import AlreadyWorkingWithTherapistPage from '../modules/onboarding/pages/AlreadyWorkingWithTherapistPage';
import { OnboardingCategoriesStepPage } from '../modules/onboarding/pages/OnboardingCategoriesStepPage';
import { OnboardingSurveyStepPage } from '../modules/onboarding/pages/OnboardingSurveyStepPage';
import OnboardingWho5IntroStepPage from '../modules/onboarding/pages/OnboardingWho5IntroStepPage';
import WelcomeUserPage from '../modules/onboarding/pages/WelcomeUserPage';
import WorkWithCoachPage from '../modules/providers/pages/WorkWithCoachPage';
import WorkWithTherapistPage from '../modules/providers/pages/WorkWithTherapistPage';
import { EmailConfirmationTokenExpiredPage } from '../modules/user/pages/EmailConfirmationTokenExpiredPage';
import EmailUnsubscribePage from '../modules/user/pages/EmailUnsubscribePage';
import ScheduleSessionCalendarPage from '../modules/videoSessions/pages/ScheduleSessionCalendarPage';
import ScheduleSessionCoachSelectionPage from '../modules/videoSessions/pages/ScheduleSessionCoachSelectionPage';
import ScheduleSessionReadonlyCalendarPage from '../modules/videoSessions/pages/ScheduleSessionReadonlyCalendarPage';
import VideoSessionPage from '../modules/videoSessions/pages/VideoSessionPage';

import { CheckForInsurancePage } from 'src/modules/insuranceIntegration/pages/CheckForInsurancePage';
import { EnterInsuranceInfoPage } from 'src/modules/insuranceIntegration/pages/EnterInsuranceInfoPage';
import { InsuranceApprovedPage } from 'src/modules/insuranceIntegration/pages/InsuranceApprovedPage';
import { Resources } from 'src/modules/resources/pages/Resources';
import { CenteredSpinner } from '../shared/components/CenteredSpinner';
import { AppLayout } from '../shared/layout/AppLayout';
import { RouteGuard } from './RouteGuard';

const ProfilePage = lazy(() => import('../modules/user/pages/ProfilePage'));
const ConnectToAnExpert = lazy(
    () => import('../modules/connectToExpert/containers/ConnectToAnExpert'),
);
const CPProgramOverview = lazy(
    () => import('../modules/programs/cpProgram/containers/CPProgramOverview'),
);
const Program = lazy(() => import('../modules/programs/containers/Program'));
const SingleMeditationPage = lazy(
    () => import('../modules/meditations/pages/SingleMeditationPage'),
);
const MeditationsPage = lazy(() => import('../modules/meditations/pages/MeditationsPage'));
const ProgramsPage = lazy(() => import('../modules/programs/pages/ProgramsPage'));

export const Routes: React.FC = () => {
    const {
        hrtoModularizedReporting,
        useRegistrationRevamp,
        healthPlanIntegrationMemberEligibility,
    } = useFlags();

    return (
        <Suspense fallback={<CenteredSpinner delay={200} />}>
            <Switch>
                <RouteGuard
                    path="/set-new-password/:uidb64?/:token?"
                    component={EapPasswordResetFormFinal}
                    specialView
                />
                <RouteGuard
                    path="/password-reset/confirm/:uidb64?/:token?"
                    component={EapPasswordResetFormFinal}
                    specialView
                />
                <RouteGuard
                    path="/email-confirmed"
                    exact
                    component={EmailAddressConfirmed}
                    specialView
                />
                <RouteGuard
                    path="/email-confirmation-token-expired"
                    exact
                    component={EmailConfirmationTokenExpiredPage}
                    specialView
                />
                <RouteGuard
                    path="/invalid-registration-token"
                    exact
                    component={RegistrationLinkNotValidPage}
                    specialView
                />
                <RouteGuard path="/ineligible" exact component={IneligiblePage} specialView />
                <RouteGuard
                    path="/dependent-invitation"
                    exact
                    component={RegistrationPage}
                    specialView
                />
                <RouteGuard
                    path="/dependent-invitation/:uidb64"
                    exact
                    component={RegistrationForm}
                />
                <Route path="/sso-verification/:token" exact component={SingleSignOnVerify} />
                <Route path="/sso-failed" exact component={SingleSignOnFailed} />
                <RouteGuard path="/account-expired" exact component={AccountHasExpired} />

                <RouteGuard path="/rate-session" exact component={RateVideoSessionPage} secured />

                <RouteGuard
                    path="/email-preferences"
                    exact
                    component={ProvidePersonalEmailAddressPage}
                    secured
                />

                <RouteGuard path="/welcome" exact component={WelcomeUserPage} secured />
                <RouteGuard path="/onboarding-privacy" exact component={PrivacyPage} secured />
                <RouteGuard
                    path="/onboarding-categories"
                    exact
                    component={OnboardingCategoriesStepPage}
                    secured
                />
                <RouteGuard
                    path="/onboarding-wellness"
                    exact
                    component={OnboardingWellnessSurveyPage}
                    secured
                />
                <RouteGuard
                    path="/a-few-questions"
                    exact
                    component={OnboardingWho5IntroStepPage}
                    secured
                />
                <RouteGuard
                    path="/onboarding-survey"
                    exact
                    component={OnboardingSurveyStepPage}
                    secured
                />
                <RouteGuard path="/on-my-own" exact component={OnMyOwnPage} secured />
                <RouteGuard path="/join-a-circle" exact component={JoinACirclePage} secured />
                <RouteGuard
                    path="/already-working-with-therapist"
                    component={AlreadyWorkingWithTherapistPage}
                    secured
                />
                <RouteGuard
                    path="/pick-your-type-of-care"
                    exact
                    component={PickYourTypeOfCarePage}
                    secured
                />
                <RouteGuard
                    path="/get-program-recommendations"
                    exact
                    component={GetProgramRecommendationsPage}
                    secured
                />
                <RouteGuard path="/youre-on-your-way" component={YoureOnYourWay} secured />

                <RouteGuard path="/re-triage" component={ReTriageIntroPage} secured />
                <RouteGuard
                    path="/re-triage-categories"
                    component={ReTriageTopicsSelectionPage}
                    secured
                />
                <RouteGuard
                    path="/re-triage-assessment"
                    component={ReTriageAssessmentPage}
                    secured
                />
                <RouteGuard
                    path="/re-triage-therapy"
                    component={ReTriageContinueTherapyPage}
                    secured
                />
                <RouteGuard
                    path="/re-triage-next-steps"
                    component={ReTriageGreenYellowSummaryPage}
                    secured
                />
                <RouteGuard
                    path="/re-triage-care-advisor"
                    component={ReTriageRequestCareAdvisorPage}
                    secured
                />
                <RouteGuard path="/re-triage-thank-you" component={ReTriageThankYouPage} secured />

                <RouteGuard
                    path="/pre-session-assessments-intro"
                    component={PreSessionAssessmentsIntroPage}
                    secured
                />
                <RouteGuard
                    path="/pre-session-assessments"
                    component={PreSessionAssessmentsPage}
                    secured
                />
                <RouteGuard
                    path="/pre-session-assessments-score"
                    component={PreSessionAssessmentsScorePage}
                    secured
                />

                <Route path="/authenticated" exact component={PostAuth} />
                <Route path="/video-session/:uuid?" component={VideoSessionPage} />

                <Route path="/email-unsubscribe/:email" exact component={EmailUnsubscribePage} />

                <Route
                    path="/provider-dashboard"
                    component={() => {
                        window.location.href = process.env.REACT_APP_PROVIDER_DASH_URL || '/';
                        return null;
                    }}
                />

                <RouteGuard path="/registration" exact component={RegistrationPage} specialView />
                <RouteGuard path="/preferred-email" component={PreferredEmailPage} secured />
                <RouteGuard
                    path="/preferred-email-sent"
                    component={PreferredEmailSentPage}
                    secured
                />
                <RouteGuard path="/consent-form-page" component={ConsentFormPage} specialView />
                <RouteGuard path="/password-form-page" component={PasswordFormPage} specialView />
                <RouteGuard path="/completion-page" component={CompletionPage} secured />
                <RouteGuard path="/start-assessment-page" component={StartAssessmentPage} secured />

                <RouteGuard path="/registration-basics" component={RegistrationBasicsForm} />

                <AppLayout>
                    <Switch>
                        <RouteGuard
                            exact
                            path="/"
                            component={useRegistrationRevamp ? LandingPage : RegistrationForm}
                        />
                        <RouteGuard path="/login" component={LoginForm} />
                        <RouteGuard path="/password-reset" exact component={EapPasswordResetForm} />
                        <RouteGuard path="/single-sign-on" exact component={SingleSignOnForm} />
                        <RouteGuard
                            path="/wellbeing-check-in"
                            component={RetakeAssessmentsPage}
                            secured
                        />
                        <RouteGuard
                            path="/wellbeing-check-score"
                            component={WellbeingScore}
                            secured
                        />
                        <RouteGuard
                            path="/provide-additional-info"
                            component={ProvideAdditionalInfoPage}
                            secured
                        />
                        <RouteGuard path="/start" component={HomePage} secured />
                        <RouteGuard
                            path="/dashboard/:section?/:sectionState?"
                            component={HomePage}
                            secured
                        />
                        <RouteGuard path="/profile" component={ProfilePage} secured />
                        <RouteGuard
                            path="/connect-with-expert/:step?"
                            component={ConnectToAnExpert}
                            secured
                        />
                        <RouteGuard
                            path="/core-pillars-overview"
                            exact
                            component={CPProgramOverview}
                            secured
                        />
                        <RouteGuard
                            path="/pause/complete"
                            component={DailyPauseCelebration}
                            exact
                            secured
                        />
                        <RouteGuard path="/pause/:uuid?" component={DailyPause} secured />
                        <RouteGuard
                            path="/programs/:programId/:weekId/:stepId?"
                            component={Program}
                            secured
                        />
                        <RouteGuard path="/course/:slug" component={CoursePage} secured />
                        <RouteGuard path="/program/:slug" component={NewProgram} secured />
                        <RouteGuard path="/programs" component={ProgramsPage} secured />
                        <RouteGuard path="/meditations" component={MeditationsPage} secured />
                        <RouteGuard path="/resources" component={Resources} secured />
                        <RouteGuard
                            path="/meditation/:slug"
                            component={SingleMeditationPage}
                            secured
                        />
                        <RouteGuard
                            path="/work-with-a-therapist"
                            exact
                            component={WorkWithTherapistPage}
                            secured
                        />
                        <RouteGuard
                            path="/work-with-a-coach"
                            exact
                            component={WorkWithCoachPage}
                            secured
                        />
                        <RouteGuard
                            path="/work-with-a-mental-health-coach"
                            exact
                            component={WorkWithMentalHealthCoachPage}
                            secured
                        />
                        <RouteGuard
                            path="/schedule-session/select-coach"
                            component={ScheduleSessionCoachSelectionPage}
                            secured
                        />
                        <RouteGuard
                            // If the user has submitted their intake form to coach, the IntakeForm page
                            // will send the user on to the appropriate flow unless `reviewIntent` is "review",
                            // in which case the user will be show the intake form review screen.
                            path="/care-form/:coachingAssignmentUuid?/:reviewIntent?"
                            exact
                            component={IntakeFormPage}
                            secured
                        />
                        <RouteGuard
                            path="/inquire-employer-insurance-coverage"
                            exact
                            component={
                                healthPlanIntegrationMemberEligibility
                                    ? CheckForInsurancePage
                                    : () => <Redirect to="/dashboard" />
                            }
                            secured
                        />
                        <RouteGuard
                            path="/enter-insurance-plan-information"
                            exact
                            component={
                                healthPlanIntegrationMemberEligibility
                                    ? EnterInsuranceInfoPage
                                    : () => <Redirect to="/dashboard" />
                            }
                            secured
                        />
                        <RouteGuard
                            path="/insurance-approved"
                            exact
                            component={
                                healthPlanIntegrationMemberEligibility
                                    ? InsuranceApprovedPage
                                    : () => <Redirect to="/dashboard" />
                            }
                            secured
                        />
                        {/* Member-facing scheduling */}
                        <RouteGuard
                            path="/schedule-session/calendar"
                            component={ScheduleSessionCalendarPage}
                            secured
                        />
                        {/* From provider dashboard for scheduling preview */}
                        <RouteGuard
                            path="/availability-readonly/:coachUuid"
                            component={ScheduleSessionReadonlyCalendarPage}
                            redirectTo="/login"
                            secured
                        />
                        <RouteGuard
                            path="/admin"
                            component={
                                hrtoModularizedReporting
                                    ? AdminDashboardPage
                                    : LegacyAdminDashboardPage
                            }
                            redirectTo="/login"
                            exact
                            secured
                        />
                        <RouteGuard
                            path="/admin/reporting"
                            component={AdminReportsPage}
                            redirectTo="/login"
                            exact
                            secured
                        />
                        <RouteGuard
                            path="/standard-assessment"
                            component={RetakeAssessmentsPage}
                            secured
                            additionalProps={{
                                isFullAssessment: true,
                            }}
                        />
                        <RouteGuard
                            path="/extended-assessment"
                            component={RetakeAssessmentsPage}
                            secured
                            additionalProps={{
                                isFullAssessment: true,
                            }}
                        />
                        <Redirect to="/dashboard" />
                    </Switch>
                </AppLayout>
            </Switch>
        </Suspense>
    );
};
